// How a table's rows read on its tile: the data stays as it came (agents read
// it whole), and the tile shows it as a person would. A column of timestamps
// reads as dates, an amount beside a currency column as money in that
// currency (the currency column then goes), a status as a word with its tone,
// and a yes-or-no column beside a status that already says it goes.

export interface ViewColumn {
  key: string;
  title: string;
  kind: "date" | "money" | "number" | "status" | "flag" | "text";
}

const ISO = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
const MONEY = /^(total|amount|price|subtotal|revenue|net|gross|paid|refunded_amount|fee|tax|discount)(_usd)?$/i;
const STATUS = /^(status|state)$/i;

/** "refunded_amount" reads "Refunded amount". */
const titleOf = (k: string) => {
  const w = k.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
};

/** The columns a table shows, each with how its cells read. */
export function viewColumns(cols: string[], rows: Record<string, unknown>[]): ViewColumn[] {
  const sample = rows.slice(0, 30);
  const all = (c: string, test: (v: unknown) => boolean) => sample.some((r) => r[c] !== null && r[c] !== undefined && r[c] !== "") && sample.every((r) => r[c] === null || r[c] === undefined || r[c] === "" || test(r[c]));
  const currency = cols.find((c) => /^currency$/i.test(c) && all(c, (v) => typeof v === "string" && /^[A-Za-z]{3}$/.test(v)));
  const status = cols.find((c) => STATUS.test(c) && all(c, (v) => typeof v === "string"));
  const out: ViewColumn[] = [];
  for (const c of cols) {
    if (c === currency) continue;
    // a yes-or-no a status already says (refunded beside status refunded) is said once
    if (status && all(c, (v) => typeof v === "boolean") && sample.every((r) => !r[c] || String(r[status]).toLowerCase().includes(c.toLowerCase().replace(/^is_?/, "")) || String(r[status]) === "void")) continue;
    const kind: ViewColumn["kind"] = all(c, (v) => typeof v === "string" && ISO.test(v) && Number.isFinite(Date.parse(v)))
      ? "date"
      : currency && MONEY.test(c) && all(c, (v) => typeof v === "number")
        ? "money"
        : all(c, (v) => typeof v === "number")
          ? "number"
          : c === status
            ? "status"
            : all(c, (v) => typeof v === "boolean")
              ? "flag"
              : "text";
    out.push({ key: c, title: titleOf(c), kind });
  }
  return out;
}

/** The currency a row's money is in, from its currency column. */
export function rowCurrency(row: Record<string, unknown>): string | null {
  const k = Object.keys(row).find((c) => /^currency$/i.test(c));
  const v = k ? row[k] : null;
  return typeof v === "string" && /^[A-Za-z]{3}$/.test(v) ? v.toUpperCase() : null;
}

/** "Oct 14, 6:26 PM", or "Oct 14" for a date with no time, in the viewer's time; the year only when it is not this one. */
export function dateCell(v: string, now = Date.now()): string {
  const t = Date.parse(v);
  const d = new Date(t);
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(v);
  const year = (dateOnly ? d.getUTCFullYear() : d.getFullYear()) !== new Date(now).getFullYear() ? "numeric" : undefined;
  if (dateOnly) return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year, timeZone: "UTC" });
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year })}, ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

/** A status's tone: paid and active are good, refunded and failed bad, pending and the rest in between. */
export function statusTone(v: string): "ok" | "warn" | "bad" | null {
  const s = v.toLowerCase();
  if (/^(paid|active|succeeded|success|complete|completed|ok|up|live)$/.test(s)) return "ok";
  if (/^(refunded|failed|fraudulent|void|cancelled|canceled|expired|unpaid|down|error)$/.test(s)) return "bad";
  if (/^(pending|partial_refund|past_due|on_trial|trialing|paused|processing|draft|open)$/.test(s)) return "warn";
  return null;
}

/** "partial_refund" reads "Partly refunded"; any other status by its own words. */
export function statusText(v: string): string {
  if (v.toLowerCase() === "partial_refund") return "Partly refunded";
  const w = v.replace(/_/g, " ");
  return w.charAt(0).toUpperCase() + w.slice(1);
}
